import { useEffect, useRef, useState, type ReactNode } from 'react';

import { colors } from '@design-tokens/colors';
import { useTheme } from '@contexts/ThemeContext';
import {
  CHURCH_MAP_LOCATION,
  CHURCH_MAP_ZOOM,
  getMapEmbedViewUrl,
} from '@utils/landingMapConfig';
import { resolveGoogleMapId } from '@utils/googleMapId';
import { loadGoogleMapsJsApi } from '@utils/loadGoogleMaps';

export interface ContactMapProps {
  /** Shown when the API key is missing */
  fallback: ReactNode;
}

/**
 * Interactive map for the landing contact section.
 * Uses AdvancedMarkerElement (Maps JavaScript API). Falls back to Embed API iframe when
 * the JS API or map ID is unavailable.
 */
export function ContactMap({ fallback }: ContactMapProps) {
  const { isDark } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  /** Progress of the Maps JavaScript API map (used when both an API key and a map ID exist). */
  const [jsMode, setJsMode] = useState<'loading' | 'js' | 'embed'>('loading');

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_EMBED_API_KEY;
  const mapId = resolveGoogleMapId();

  let mode: 'loading' | 'js' | 'embed' | 'none';
  if (!apiKey) {
    mode = 'none';
  } else if (!mapId) {
    mode = 'embed';
  } else {
    mode = jsMode;
  }
  const embedUrl = mode === 'embed' && apiKey ? getMapEmbedViewUrl(apiKey) : null;

  useEffect(() => {
    if (!apiKey || !mapId) {
      return;
    }

    let cancelled = false;
    const container = containerRef.current;

    const initJsMap = async () => {
      try {
        await loadGoogleMapsJsApi(apiKey);
        if (cancelled || !container) {
          return;
        }

        const map = new google.maps.Map(container, {
          center: CHURCH_MAP_LOCATION,
          zoom: CHURCH_MAP_ZOOM,
          mapId,
          colorScheme: isDark
            ? google.maps.ColorScheme.DARK
            : google.maps.ColorScheme.LIGHT,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
        });

        const pin = new google.maps.marker.PinElement({
          background: colors.primary[500],
          borderColor: colors.primary[700],
          glyphColor: colors.base.white,
        });

        const marker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: CHURCH_MAP_LOCATION,
          title: 'Bethlehem Baptist Church',
          content: pin.element,
        });

        mapRef.current = map;
        markerRef.current = marker;
        setJsMode('js');
      } catch {
        if (!cancelled) {
          setJsMode('embed');
        }
      }
    };

    void initJsMap();

    return () => {
      cancelled = true;
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      mapRef.current = null;
      container?.replaceChildren();
    };
  }, [apiKey, mapId, isDark]);

  if (mode === 'none') {
    return <>{fallback}</>;
  }

  if (mode === 'embed' && embedUrl) {
    return (
      <div className="bhg-landing-contact__map-viewport bhg-landing-contact__map-viewport--embed">
        <iframe
          title="Map showing Bethlehem Baptist Church, Knightdale NC"
          src={embedUrl}
          className="bhg-landing-contact__map-iframe"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    );
  }

  return (
    <div
      key={isDark ? 'dark' : 'light'}
      ref={containerRef}
      className="bhg-landing-contact__map-canvas"
      role="img"
      aria-label="Map showing Bethlehem Baptist Church at 8400 Poole Road, Knightdale NC"
    />
  );
}
